/**
 * Copyright 2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { OptimizelyDecideOption } from "../../../shared_types";
import { v4 as uuidV4 } from 'uuid';
import murmurhash from "murmurhash";
import { SerialRunner } from "../../../utils/executor/serial_runner";
import { CMAB_CACHE_ATTRIBUTES_MISMATCH, CMAB_CACHE_HIT, CMAB_CACHE_MISS, IGNORE_CMAB_CACHE, INVALIDATE_CMAB_CACHE, RESET_CMAB_CACHE, } from 'log_message';
const SERIALIZER_BUCKETS = 1000;
const LOGGER_NAME = 'CmabService';
export class DefaultCmabService {
    constructor(options) {
        var _a;
        this.serializers = Array.from({ length: SERIALIZER_BUCKETS }, () => new SerialRunner());
        this.cmabCache = options.cmabCache;
        this.cmabClient = options.cmabClient;
        this.logger = options.logger;
        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.setName(LOGGER_NAME);
    }
    getSerializerIndex(userId, experimentId) {
        const key = this.getCacheKey(userId, experimentId);
        const hash = murmurhash.v3(key);
        return Math.abs(hash) % SERIALIZER_BUCKETS;
    }
    getDecision(projectConfig, userContext, ruleId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const serializerIndex = this.getSerializerIndex(userContext.getUserId(), ruleId);
            return this.serializers[serializerIndex].run(() => this.getDecisionInternal(projectConfig, userContext, ruleId, options));
        });
    }
    getDecisionInternal(projectConfig, userContext, ruleId, options) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const userId = userContext.getUserId();
            const filteredAttributes = this.filterAttributes(projectConfig, userContext, ruleId);
            if (options[OptimizelyDecideOption.IGNORE_CMAB_CACHE]) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.debug(IGNORE_CMAB_CACHE, userId, ruleId);
                return this.fetchDecision(ruleId, userId, filteredAttributes);
            }
            if (options[OptimizelyDecideOption.RESET_CMAB_CACHE]) {
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.debug(RESET_CMAB_CACHE, userId, ruleId);
                this.cmabCache.reset();
            }
            const cacheKey = this.getCacheKey(userId, ruleId);
            if (options[OptimizelyDecideOption.INVALIDATE_USER_CMAB_CACHE]) {
                (_c = this.logger) === null || _c === void 0 ? void 0 : _c.debug(INVALIDATE_CMAB_CACHE, userId, ruleId);
                this.cmabCache.remove(cacheKey);
            }
            const cachedValue = yield this.cmabCache.lookup(cacheKey);
            const attributesJson = JSON.stringify(filteredAttributes, Object.keys(filteredAttributes).sort());
            const attributesHash = String(murmurhash.v3(attributesJson));
            if (cachedValue) {
                if (cachedValue.attributesHash === attributesHash) {
                    (_d = this.logger) === null || _d === void 0 ? void 0 : _d.debug(CMAB_CACHE_HIT, userId, ruleId);
                    return { variationId: cachedValue.variationId, cmabUuid: cachedValue.cmabUuid };
                }
                else {
                    (_e = this.logger) === null || _e === void 0 ? void 0 : _e.debug(CMAB_CACHE_ATTRIBUTES_MISMATCH, userId, ruleId);
                    this.cmabCache.remove(cacheKey);
                }
            }
            else {
                (_f = this.logger) === null || _f === void 0 ? void 0 : _f.debug(CMAB_CACHE_MISS, userId, ruleId);
            }
            const variation = yield this.fetchDecision(ruleId, userId, filteredAttributes);
            this.cmabCache.save(cacheKey, {
                attributesHash,
                variationId: variation.variationId,
                cmabUuid: variation.cmabUuid,
            });
            return variation;
        });
    }
    fetchDecision(ruleId, userId, attributes) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmabUuid = uuidV4();
            const variationId = yield this.cmabClient.fetchDecision(ruleId, userId, attributes, cmabUuid);
            return { variationId, cmabUuid };
        });
    }
    filterAttributes(projectConfig, userContext, ruleId) {
        const filteredAttributes = {};
        const userAttributes = userContext.getAttributes();
        const experiment = projectConfig.experimentIdMap[ruleId];
        if (!experiment || !experiment.cmab) {
            return filteredAttributes;
        }
        const cmabAttributeIds = experiment.cmab.attributeIds;
        cmabAttributeIds.forEach((aid) => {
            const attribute = projectConfig.attributeIdMap[aid];
            if (userAttributes.hasOwnProperty(attribute.key)) {
                filteredAttributes[aid] = userAttributes[attribute.key];
            }
        });
        return filteredAttributes;
    }
    getCacheKey(userId, ruleId) {
        const len = userId.length;
        return `${len}-${userId}-${ruleId}`;
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=cmab_service.js.map